import { AQI_CATEGORIES, POLLUTANT_SHORT } from "../../../shared/air";
import { todayIn, zonedParts } from "../../../shared/dates";
import { uvCategory, uvPeak } from "../../../shared/sky/uv";
import { compassPoint, formatTemp } from "../../../shared/weather";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { useAir, useGrid, useObsDetail } from "../../data/weatherQueries";
import { Icon } from "../../ui/Icon";
import { EmptyState } from "../../ui/StatusBadge";
import { relativeAge, useNow } from "../../ui/time";
import { WeatherIcon } from "../../ui/WeatherIcon";
import { useUv } from "../sky/api";
import { NeedsLocation, remPx, SkyFoot, useMeasure, useTimeFmt } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { ArcGauge, GaugeTile, RainTube, SkyColumn, WindCompass, type SkyLayer } from "./gauges";
import { AQI_BANDS, aqiFraction, beaufort, pressureTrendWord, UV_BANDS } from "./scales";
import { CLOUD_NAMES, dewComfort, units } from "./units";
import { highLow, useWeatherSummary } from "./WeatherWidget";
import "./nerd.css";

type ConditionsConfig = Record<string, never>;

// Barometer face: 970–1050 hPa (28.6–31.0 inHg) covers nearly every reading on record.
const P_MIN = 970;
const P_MAX = 1050;
const COVER: Record<string, number> = { FEW: 0.25, SCT: 0.5, BKN: 0.75, OVC: 1, VV: 1 };

/** Gauge tiles in priority order; small cards show the first ones that fit. */
type TileKey = "wind" | "pressure" | "humidity" | "aqi" | "uv" | "rain" | "sky";
const TILE_ORDER: TileKey[] = ["wind", "pressure", "humidity", "aqi", "uv", "rain", "sky"];

/** The nearest station's report as an instrument panel: temperature up top, then a dial per reading. */
function ConditionsDetail({ size, fullscreen, openFullscreen }: WidgetProps<ConditionsConfig>) {
  const { settings, timeZone } = useHousehold();
  const now = useNow("minute");
  const fmt = useTimeFmt();
  const wx = useWeatherSummary();
  const obs = wx.q.data?.observation ?? null;
  const detail = useObsDetail(obs?.station.id ?? null);
  const air = useAir();
  const uvQ = useUv();
  const grid = useGrid();
  const [gridRef, box] = useMeasure<HTMLDivElement>();
  const state = dataStateOf(wx.q, 30 * 60_000);
  if (!settings.location) return <NeedsLocation icon="gauge" title="Conditions need a home location" detail="Set a general location to see station readings." compact={size.variant === "compact"} />;
  if (!wx.q.data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="gauge" title="Conditions unavailable" detail={wx.q.error?.message} />;
  if (!obs) return <EmptyState icon="gauge" title="No station reading right now" detail={wx.q.data.observationNote ?? undefined} />;

  const unit = settings.temperatureUnit;
  const us = unit === "F";
  const u = units(unit);
  const d = detail.data;
  const rem = remPx();
  const { high, low } = highLow(wx.q.data.daily, unit);

  // ----- Readings -----
  const calm = obs.windSpeedKph === null || obs.windSpeedKph < 1;
  const pressureHpa = d?.pressureHpa ?? (obs.pressurePa === null ? null : obs.pressurePa / 100);
  const change = d?.pressureChange3hHpa ?? null;
  const trend = pressureTrendWord(change);
  const pFrac = (hpa: number) => (hpa - P_MIN) / (P_MAX - P_MIN);

  const airData = air.data?.status === "ok" ? air.data : null;
  const aq = airData?.readings[0] ?? null;
  const aqCat = aq ? AQI_CATEGORIES[aq.category] : null;

  const uvData = uvQ.data && uvQ.data.status !== "unsupported" && uvQ.data.date === todayIn(timeZone, now) ? uvQ.data : null;
  const hourNow = Math.floor(zonedParts(now, timeZone).minutes / 60);
  const uvNow = uvData ? (uvData.hours.find((h) => h.hour === hourNow)?.uv ?? 0) : null;
  const uvTop = uvData ? uvPeak(uvData.hours) : null;
  const uvDone = uvData ? uvData.hours.length > 0 && hourNow > uvData.hours[uvData.hours.length - 1]!.hour : false;

  const nextHours = grid.data?.hours.filter((h) => Date.parse(h.t) >= now.getTime() - 3_600_000).slice(0, 24) ?? [];
  const rain24 = nextHours.reduce((s, h) => s + (h.qpfMm ?? 0), 0);
  const pop24 = nextHours.length ? Math.max(0, ...nextHours.map((h) => h.pop ?? 0)) : null;
  const snow24 = nextHours.reduce((s, h) => s + (h.snowMm ?? 0), 0);
  const rainScale = us ? 25.4 : 25; // 1 in / 25 mm, doubling for wetter days
  const rainMax = rain24 > rainScale ? Math.ceil(rain24 / rainScale) * rainScale : rainScale;

  const layers: SkyLayer[] = (d?.cloudLayers ?? [])
    .filter((l) => COVER[l.amount] !== undefined && l.baseM !== null)
    .slice(0, 3)
    .map((l) => ({ cover: COVER[l.amount]!, name: CLOUD_NAMES[l.amount] ?? l.amount, height: u.heightLabel(l.baseM) }));
  const ceiling = d?.cloudLayers.find((l) => ["BKN", "OVC", "VV"].includes(l.amount));

  // ----- Arrangement -----
  // By real size, not the variant: phones stack every card as "compact" but give this one room for its dials.
  const compact = size.height < rem * 15 || size.width < rem * 17;
  // Wide, short cards put the temperature beside the dials instead of above them.
  const side = !compact && size.width / size.height > 1.9 && size.width > rem * 44;
  const minW = rem * 8.6;
  const minH = rem * 10.4;
  const gap = rem * 0.6;
  const maxCols = Math.max(1, Math.floor((box.width + gap) / (minW + gap)));
  const maxRows = Math.max(1, Math.floor((box.height + gap) / (minH + gap)));
  const count = box.width ? Math.min(TILE_ORDER.length, maxCols * maxRows) : 0;
  // Of the grids that hold them all, take the one with the biggest dials.
  let cols = 1;
  let rows = 1;
  let best = -Infinity;
  for (let c = 1; c <= Math.min(count, maxCols); c++) {
    const r = Math.ceil(count / c);
    if (r > maxRows) continue;
    const dial = Math.min((box.width - gap * (c - 1)) / c - rem * 1.3, (box.height - gap * (r - 1)) / r - rem * 4.2);
    const score = dial - (c * r - count) * rem * 0.5;
    if (score > best) [best, cols, rows] = [score, c, r];
  }
  const tiles = TILE_ORDER.slice(0, count);

  const tile = (k: TileKey) => {
    switch (k) {
      case "wind":
        return (
          <GaugeTile key={k} icon="wind" label="Wind" note={calm ? beaufort(obs.windSpeedKph) : `From the ${compassPoint(obs.windDirectionDeg)} · ${beaufort(obs.windSpeedKph).toLowerCase()}`}>
            <WindCompass deg={obs.windDirectionDeg} calm={calm} label={calm ? "Calm" : `Wind from the ${compassPoint(obs.windDirectionDeg)} at ${u.speedLabel(obs.windSpeedKph)}`}>
              <strong className="g-val">{calm ? "0" : Math.round(u.speed(obs.windSpeedKph!))}</strong>
              <span className="g-unit">{u.speedUnit}</span>
              {obs.windGustKph ? <span className="g-sub g-gust">gusts {Math.round(u.speed(obs.windGustKph))}</span> : null}
            </WindCompass>
          </GaugeTile>
        );
      case "pressure":
        return (
          <GaugeTile
            key={k}
            icon="gauge"
            label="Pressure"
            note={trend ? `${trend.word}${change !== null && trend.dir !== "flat" ? ` · ${change > 0 ? "+" : "−"}${u.pressureDelta(change).replace(" inHg", "″").replace(" hPa", "")} in 3 h` : ""}` : "No trend yet"}
          >
            <ArcGauge
              value={pressureHpa === null ? null : pFrac(pressureHpa)}
              ghost={pressureHpa !== null && change !== null ? pFrac(pressureHpa - change) : null}
              needle
              ticks={8}
              ends={us ? ["28.6", "31.0"] : ["970", "1050"]}
              label={`Pressure ${u.pressureLabel(pressureHpa)}${trend ? `, ${trend.word.toLowerCase()}` : ""}`}
            >
              <strong className="g-val g-val-sm">{pressureHpa === null ? "—" : us ? (pressureHpa * 0.02953).toFixed(2) : Math.round(pressureHpa)}</strong>
              <span className="g-unit">
                {us ? "inHg" : "hPa"}
                {trend && trend.dir !== "flat" && <span className={`g-trend ${trend.dir}`}>{trend.dir === "up" ? " ▲" : " ▼"}</span>}
              </span>
            </ArcGauge>
          </GaugeTile>
        );
      case "humidity":
        return (
          <GaugeTile key={k} icon="drop" label="Humidity" note={obs.dewpointC === null ? "Dew point —" : `Dew point ${formatTemp(obs.dewpointC, unit)} · ${dewComfort(obs.dewpointC)?.toLowerCase()}`}>
            <ArcGauge value={obs.humidityPct === null ? null : obs.humidityPct / 100} fill="var(--rain)" ends={["0", "100"]} label={`Humidity ${obs.humidityPct === null ? "unknown" : `${Math.round(obs.humidityPct)}%`}`}>
              <strong className="g-val">{obs.humidityPct === null ? "—" : Math.round(obs.humidityPct)}</strong>
              <span className="g-unit">%</span>
            </ArcGauge>
          </GaugeTile>
        );
      case "aqi":
        return (
          <GaugeTile
            key={k}
            icon="eye"
            label="Air quality"
            tone={aqCat?.color}
            note={aq && aqCat ? `${aqCat.name} · ${POLLUTANT_SHORT[aq.parameter] ?? aq.parameter}` : air.data?.status === "unavailable" || air.isError ? "Not available" : airData ? "No monitor nearby" : "Loading…"}
          >
            <ArcGauge value={aq ? aqiFraction(aq.aqi) : null} bands={AQI_BANDS} ends={["0", "300+"]} label={aq && aqCat ? `Air quality index ${aq.aqi}, ${aqCat.name}` : "Air quality index unavailable"}>
              <strong className="g-val">{aq ? aq.aqi : "—"}</strong>
              <span className="g-unit">AQI</span>
            </ArcGauge>
          </GaugeTile>
        );
      case "uv": {
        const cat = uvNow !== null ? uvCategory(uvNow) : null;
        return (
          <GaugeTile
            key={k}
            icon="sun"
            label="UV index"
            tone={cat?.color}
            note={uvData ? (uvDone ? "Done for today" : uvTop && uvTop.uv > 0 ? `${cat!.label} · peak ${uvTop.uv} at ${fmt.h24 ? `${uvTop.hour}:00` : `${uvTop.hour % 12 || 12} ${uvTop.hour < 12 ? "AM" : "PM"}`}` : cat!.label) : uvQ.data ? "No forecast today" : "Loading…"}
          >
            <ArcGauge value={uvNow === null ? null : uvNow / 12} bands={UV_BANDS} ends={["0", "11+"]} label={uvNow === null ? "UV index unavailable" : `UV index ${uvNow}, ${cat!.label}`}>
              <strong className="g-val">{uvNow ?? "—"}</strong>
              <span className="g-unit">UV</span>
            </ArcGauge>
          </GaugeTile>
        );
      }
      case "rain":
        return (
          <GaugeTile key={k} icon="umbrella" label="Rain, next 24 h" note={pop24 === null ? "Forecast loading…" : snow24 >= 2.5 ? `Snow ${u.snowLabel(snow24)} · ${pop24}% chance` : `Chance up to ${pop24}%`}>
            <RainTube fraction={rain24 / rainMax} label={`Forecast rain next 24 hours ${u.rainLabel(rain24)}`}>
              <strong className="g-val">{us ? (rain24 / 25.4).toFixed(2) : rain24.toFixed(rain24 >= 10 ? 0 : 1)}</strong>
              <span className="g-unit">{us ? "in" : "mm"}</span>
              {(d?.precipLast6hMm ?? 0) > 0 && <span className="g-sub">last 6 h {u.rainLabel(d!.precipLast6hMm!)}</span>}
            </RainTube>
          </GaugeTile>
        );
      case "sky":
        return (
          <GaugeTile key={k} icon="cloud" label="Sky" note={ceiling ? `Ceiling ${u.heightLabel(ceiling.baseM)}` : layers.length ? "No ceiling" : d ? "Clear" : "—"}>
            <SkyColumn layers={layers} label={`Visibility ${u.distanceLabel(obs.visibilityM)}`}>
              <strong className="g-val g-val-sm">{u.distanceLabel(obs.visibilityM).replace(/ (mi|km)$/, "")}</strong>
              <span className="g-unit">{us ? "mi visibility" : "km visibility"}</span>
            </SkyColumn>
          </GaugeTile>
        );
    }
  };

  const feels = obs.feelsLikeC !== null && Math.abs(obs.feelsLikeC - obs.temperatureC) >= 1 ? obs.feelsLikeC : null;
  const hero = (
    <div className="st-hero">
      <WeatherIcon kind={obs.icon} isDaytime={obs.isDaytime} size="3.6rem" />
      <span className="st-temp">{formatTemp(obs.temperatureC, unit)}</span>
      <span className="st-desc">
        <strong>{obs.description ?? "—"}</strong>
        <span className="muted">
          {feels !== null ? `Feels like ${formatTemp(feels, unit)}` : null}
          {feels !== null && (high || low) ? " · " : null}
          {high && `H ${high}`}
          {high && low && "  "}
          {low && `L ${low}`}
        </span>
        {(d?.high24hC != null || d?.low24hC != null) && (
          <span className="muted st-24">
            Last 24 h {u.tempLabel(d?.low24hC ?? null)} – {u.tempLabel(d?.high24hC ?? null)}
          </span>
        )}
      </span>
    </div>
  );

  return (
    <div className="nerd nerd-now">
      <WidgetHeader title="Right now" meta={`${obs.station.name} · ${relativeAge(obs.observedAt)}`} onTitle={fullscreen ? undefined : openFullscreen} />
      {compact ? (
        <div className="st-compact">
          {hero}
          <p className="st-line">
            <span>
              <Icon name="wind" size="1em" /> {calm ? "Calm" : `${compassPoint(obs.windDirectionDeg)} ${u.speedLabel(obs.windSpeedKph)}`}
              {obs.windGustKph ? `, gusts ${Math.round(u.speed(obs.windGustKph))}` : ""}
            </span>
            <span>
              <Icon name="drop" size="1em" /> {obs.humidityPct === null ? "—" : `${Math.round(obs.humidityPct)}%`}
            </span>
            <span>
              <Icon name="gauge" size="1em" /> {u.pressureLabel(pressureHpa)}
            </span>
          </p>
        </div>
      ) : (
        <div className={`st-body ${side ? "is-side" : ""}`}>
          {hero}
          <div className="st-gauges" ref={gridRef} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }}>
            {tiles.map(tile)}
          </div>
        </div>
      )}
      {!compact && (
        <SkyFoot
          source={`NWS ${obs.station.id}, ${us ? `${Math.max(1, Math.round(obs.station.distanceKm * 0.621))} mi` : `${Math.max(1, Math.round(obs.station.distanceKm))} km`} away${tiles.includes("aqi") && airData ? " · EPA AirNow" : ""}${tiles.includes("uv") && uvData ? " · EPA UV" : ""}`}
          stamp={d?.stamp}
          state={state}
          updatedAt={wx.q.dataUpdatedAt}
        />
      )}
    </div>
  );
}

export const conditionsDetailWidget: WidgetDefinition<ConditionsConfig> = {
  type: "conditions-detail",
  version: 1,
  name: "Right now, in detail",
  description: "The nearest station as an instrument panel: wind compass with gusts, barometer with its 3-hour trend, humidity and dew point, air quality, UV, rain and cloud layers.",
  icon: "gauge",
  category: "weather",
  defaultSize: { w: 5, h: 7 },
  minSize: { w: 3, h: 3 },
  stackHeight: 780,
  defaults: {},
  settings: [],
  provider: "nws",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: ConditionsDetail,
};
